import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { EntitlementDetails, PromoCodeDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { Text } from '@atlaskit/primitives';
import { FormData } from './change-subscription-pane';
import { ManagePromotionField, PROMO_CODE_KEY_PREFIX } from './manage-promotion-field';
import './change-subscription-pane.scss';

interface Props {
  formState: FormData;
  entitlement: EntitlementDetails;
  updateFormState: (data: FormData) => void;
  validPromoCodeData: Partial<PromoCodeDetails[]>;
  setValidPromoCodeData: (data: Partial<PromoCodeDetails[]>) => void;
  activePromoCodeData: Partial<PromoCodeDetails[]>;
  setActivePromoCodeData: (data: Partial<PromoCodeDetails[]>) => void;
  isDesignIteration1Enabled?: boolean;
}

// TO IMPROVE: regexForPromoCodeKey can be moved to a constant file
export const regexForPromoCodeKey = new RegExp(`^${PROMO_CODE_KEY_PREFIX}`);

export const ManagePromotion = ({
  formState,
  entitlement,
  updateFormState,
  validPromoCodeData,
  setValidPromoCodeData,
  activePromoCodeData,
  setActivePromoCodeData,
  isDesignIteration1Enabled,
}: Props) => {
  const promoCodes = [];

  for (const key in formState) {
    // Note: Applied promo code is stored as promoCode-0
    if (regexForPromoCodeKey.test(key)) {
      // @ts-ignore
      formState[key] && promoCodes.push(formState[key]);
    }
  }

  // Push at least one promo code text field
  promoCodes.length === 0 && promoCodes.push('');

  const initialFormValues = promoCodes.map((code) => {
    // Note: UUID is needed to make sure that React `key` is unique on changes,
    // otherwise when removing a form field, error and button states do not reset correctly.
    return {
      id: uuidv4(),
      value: code,
    };
  });
  const [formValues, setFormValues] = useState(initialFormValues);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
    const newFormValues = [...formValues];

    newFormValues[i]['value'] = e.target.value;
    setFormValues(newFormValues);
  };

  const addFormFields = () => {
    setFormValues([
      ...formValues,
      {
        id: uuidv4(),
        value: '',
      },
    ]);
  };

  const handleRemove = (i: number) => {
    // To improve: Coded earlier for multiple formValues, which is not the case now. Logic can be improved.
    const newFormValues = [...formValues];

    newFormValues.splice(i, 1);
    setFormValues(newFormValues);

    const currentPromoCodeState: any = {};
    newFormValues.forEach((formValue, i) => {
      currentPromoCodeState[PROMO_CODE_KEY_PREFIX + i] = formValue.value;
    });

    // Note: As `updateFormState` deep clones the sent object, in case of remove, existing keys are not
    // replaced. Hence, marking all of them as undefined and then sending the modified promo code keys.
    const formStateWithUndefinedPromo: { [key: string]: undefined } = {};
    for (const key in formState) {
      // Save all promo codes keys from form state as undefined in formStateWithUndefinedPromo
      if (regexForPromoCodeKey.test(key)) {
        formStateWithUndefinedPromo[key] = undefined;
      }
    }
    // Update `formState` with new promo code keys after remove operation
    updateFormState({ ...formStateWithUndefinedPromo, ...currentPromoCodeState });
  };

  const shouldShowAddPromoCTA = !formValues?.length;

  const renderHeader = () => {
    if (isDesignIteration1Enabled) {
      return (
        <Text size="small" color="color.text.subtle" weight="bold">
          Vendor promocode
        </Text>
      );
    }
    return <h2>Add vendor promotion</h2>;
  };

  return (
    <div className="promotion-view-section">
      {renderHeader()}
      {formValues.map((formValue, index) => (
        <ManagePromotionField
          key={formValue.id}
          index={index}
          formValue={formValue.value}
          entitlement={entitlement}
          handleChange={handleChange}
          handleRemove={handleRemove}
          validPromoCodeData={validPromoCodeData}
          setValidPromoCodeData={setValidPromoCodeData}
          activePromoCodeData={activePromoCodeData}
          setActivePromoCodeData={setActivePromoCodeData}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
        />
      ))}
      {shouldShowAddPromoCTA && (
        <Button
          iconBefore={<AddIcon label="Add promo code" size="small" />}
          appearance="link"
          spacing="none"
          className="mt-12"
          testId="add-promo-code"
          onClick={addFormFields}
        >
          Add vendor promotion
        </Button>
      )}
    </div>
  );
};
